import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Button, Alert, Form, Spinner, Row, Col, Modal, Tabs, Tab, ListGroup, Accordion } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Castaway, League } from '../types';
import '../App.css';

interface Tribe {
  name: string;
  color: string;
}

interface NewCastaway {
  name: string;
  age: string;
  occupation: string;
  tribe: string;
  imageUrl: string;
}

interface ResearchSource {
  title: string;
  uri: string;
}

const AdminLeagueManagement: React.FC = () => {
  // Common State
  const [leagues, setLeagues] = useState<League[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedSeason, setSelectedSeason] = useState<number>(() => {
    const saved = localStorage.getItem('adminSelectedSeason');
    return saved ? parseInt(saved) : 49;
  });

  // Create League State
  const [newLeagueName, setNewLeagueName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newCommissioner, setNewCommissioner] = useState('');
  const [newLeagueSeason, setNewLeagueSeason] = useState(selectedSeason);
  const [newTribes, setNewTribes] = useState<Tribe[]>([{ name: '', color: '#ffffff' }]);
  const [newCastaways, setNewCastaways] = useState<NewCastaway[]>([{ name: '', age: '', occupation: '', tribe: '', imageUrl: '' }]);
  const [isResearching, setIsResearching] = useState(false);
  const [researchSources, setResearchSources] = useState<ResearchSource[]>([]);

  // Edit League State (for the active tab's league)
  const [selectedLeagueId, setSelectedLeagueId] = useState<string>('');
  const [editName, setEditName] = useState('');
  const [editDeadline, setEditDeadline] = useState('');
  const [editActive, setEditActive] = useState<boolean>(false);
  const [editReadyToDraft, setEditReadyToDraft] = useState<boolean>(false);
  const [editTribes, setEditTribes] = useState<Tribe[]>([]);
  const [editCastaways, setEditCastaways] = useState<Castaway[]>([]);
  const [editCutoffs, setEditCutoffs] = useState<{ [episode: string]: string }>({});
  const [editLineupDeadlines, setEditLineupDeadlines] = useState<{ [episode: string]: string }>({});
  const [isResearchingCutoffs, setIsResearchingCutoffs] = useState(false);
  const [isResearchingLineups, setIsResearchingLineups] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const episodeNumbers = Array.from({ length: 12 }, (_, i) => i + 2); // Episodes 2-13

  const toLocalISOString = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const tzoffset = (new Date()).getTimezoneOffset() * 60000;
    const localISOTime = (new Date(date.getTime() - tzoffset)).toISOString().slice(0, -1);
    return localISOTime.substring(0, 16);
  };

  const getPreviewUrl = (imageUrl: string) => imageUrl.startsWith('https://static.wikia.nocookie.net/')
    ? `${process.env.REACT_APP_API_URL}/api/admin/image-proxy?url=${encodeURIComponent(imageUrl)}`
    : imageUrl;

  const fetchLeagues = useCallback(async (season: number) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/all`);
      if (!response.ok) throw new Error('Failed to fetch leagues');
      const data = await response.json();
      const filteredLeagues = (data.leagues || []).filter((l: any) => (l.season || 49) === season);
      setLeagues(filteredLeagues);
      
      if (filteredLeagues.length > 0 && !selectedLeagueId) {
          const first = filteredLeagues[0];
          selectLeague(first);
      }
    } catch (err: any) {
      setError(err.message || 'Could not load leagues.');
    } finally {
      setIsLoading(false);
    }
  }, [selectedLeagueId]);

  const selectLeague = (league: League) => {
    setSelectedLeagueId(league.id);
    setEditName(league.name);
    setEditDeadline(league.draftDeadline ? toLocalISOString(league.draftDeadline) : '');
    setEditActive(league.active);
    setEditReadyToDraft(league.readyToDraft ?? league.active);
    setEditTribes(league.tribes || []);
    setEditCastaways(league.castaways || []);
    setEditCutoffs(league.weeklyEliminationCutoffs || {});
    setEditLineupDeadlines(league.lineupDeadlines || {});
  };

  useEffect(() => {
    fetchLeagues(selectedSeason);

    const handleSeasonChange = () => {
      const newSeason = parseInt(localStorage.getItem('adminSelectedSeason') || '49');
      setSelectedSeason(newSeason);
      setNewLeagueSeason(newSeason);
      setSelectedLeagueId('');
      fetchLeagues(newSeason);
    };
    window.addEventListener('adminSeasonChanged', handleSeasonChange);
    return () => window.removeEventListener('adminSeasonChanged', handleSeasonChange);
  }, [fetchLeagues, selectedSeason]);

  // Create Handlers
  const handleTribeChange = (index: number, field: keyof Tribe, value: string, isNew: boolean) => {
    if (isNew) {
        const updated = [...newTribes];
        updated[index][field] = value;
        setNewTribes(updated);
    } else {
        const updated = [...editTribes];
        updated[index][field] = value;
        setEditTribes(updated);
    }
  };

  const handleNewCastawayChange = (index: number, field: keyof NewCastaway, value: string) => {
    const updated = [...newCastaways];
    updated[index] = { ...updated[index], [field]: value };
    setNewCastaways(updated);
  };

  const addTribe = (isNew: boolean) => {
    if (isNew) setNewTribes([...newTribes, { name: '', color: '#ffffff' }]);
    else setEditTribes([...editTribes, { name: '', color: '#ffffff' }]);
  };

  const removeTribe = (index: number, isNew: boolean) => {
    if (isNew) setNewTribes(newTribes.filter((_, i) => i !== index));
    else setEditTribes(editTribes.filter((_, i) => i !== index));
  };

  const handleEditCastawayChange = (index: number, field: keyof Castaway, value: string) => {
    const updated = [...editCastaways];
    updated[index] = {
      ...updated[index],
      [field]: field === 'age' || field === 'id' ? Number(value) : value,
    };
    setEditCastaways(updated);
  };

  const addEditCastaway = () => {
    setEditCastaways([...editCastaways, {
      id: 0,
      name: '',
      age: 0,
      occupation: '',
      tribe: editTribes[0]?.name || '',
      imageUrl: '',
    }]);
  };

  const removeEditCastaway = (index: number) => {
    setEditCastaways(editCastaways.filter((_, castawayIndex) => castawayIndex !== index));
  };

  const addNewCastaway = () => {
    setNewCastaways([...newCastaways, { name: '', age: '', occupation: '', tribe: '', imageUrl: '' }]);
  };

  const removeNewCastaway = (index: number) => {
    setNewCastaways(newCastaways.filter((_, i) => i !== index));
  };

  const handleResearchSeason = async () => {
    setIsResearching(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/research-season`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ season: newLeagueSeason }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Season research failed.');

      setNewTribes(data.research.tribes || []);
      setNewCastaways((data.research.castaways || []).map((castaway: any) => ({
        name: castaway.name || '',
        age: castaway.age == null ? '' : String(castaway.age),
        occupation: castaway.occupation || '',
        tribe: castaway.tribe || '',
        imageUrl: castaway.imageUrl || '',
      })));
      setResearchSources(data.sources || []);
      setSuccess('Research loaded into the form. Review every field before creating the league.');
    } catch (err: any) {
      setError(err.message || 'Season research failed.');
    } finally {
      setIsResearching(false);
    }
  };

  const handleCreateLeague = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUpdating(true);
    setError(null);
    setSuccess(null);
    try {
      const populatedCastaways = newCastaways.filter(castaway => Object.values(castaway).some(value => value.trim() !== ''));
      const incompleteCastaway = populatedCastaways.find(castaway => (
        !castaway.name.trim() ||
        !castaway.age.trim() ||
        !castaway.occupation.trim() ||
        !castaway.tribe.trim()
      ));
      if (incompleteCastaway) {
        throw new Error('Complete every survivor field or remove the empty survivor row.');
      }

      const castaways = populatedCastaways.map(castaway => ({
        ...castaway,
        age: Number(castaway.age),
      }));
      if (castaways.some(castaway => !Number.isInteger(castaway.age) || castaway.age <= 0)) {
        throw new Error('Survivor ages must be positive whole numbers.');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
            name: newLeagueName, 
            password: newPassword, 
            commissioner: newCommissioner, 
            tribes: newTribes, 
            castaways,
            season: newLeagueSeason
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to create league');
      setSuccess(`League "${data.league.name}" created successfully!`);
      setSelectedSeason(newLeagueSeason);
      localStorage.setItem('adminSelectedSeason', newLeagueSeason.toString());
      window.dispatchEvent(new Event('adminSeasonChanged'));
      setNewLeagueName('');
      setNewPassword('');
      setNewCommissioner('');
      setNewTribes([{ name: '', color: '#ffffff' }]);
      setNewCastaways([{ name: '', age: '', occupation: '', tribe: '', imageUrl: '' }]);
      fetchLeagues(newLeagueSeason);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  // Edit Handlers
  const handleUpdateLeague = async () => {
    if (!selectedLeagueId) return;
    setIsUpdating(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${selectedLeagueId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
            name: editName,
            active: editActive,
            readyToDraft: editReadyToDraft,
            draftDeadline: editDeadline ? new Date(editDeadline).toISOString() : null,
            weeklyEliminationCutoffs: editCutoffs,
            lineupDeadlines: editLineupDeadlines,
            tribes: editTribes,
            castaways: editCastaways,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update league');
      }

      setSuccess('League updated successfully!');
      fetchLeagues(selectedSeason);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteLeague = async () => {
    setShowDeleteConfirm(false);
    if (!selectedLeagueId) return;
    setIsUpdating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${selectedLeagueId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete league');
      setSuccess('League deleted successfully!');
      setSelectedLeagueId('');
      fetchLeagues(selectedSeason);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteEditCastaway = async (index: number) => {
    const castaway = editCastaways[index];
    if (castaway.id === 0) {
      removeEditCastaway(index);
      return;
    }
    if (!window.confirm(`Delete ${castaway.name}?`)) return;
    setIsUpdating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${selectedLeagueId}/castaways/${castaway.id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete castaway');
      setEditCastaways(editCastaways.filter((_, castawayIndex) => castawayIndex !== index));
      setSuccess(`${castaway.name} deleted successfully.`);
    } catch (err: any) {
      setError(err.message || 'Failed to delete castaway.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCutoffChange = (episode: string, value: string) => {
    if (value) {
      const localDate = new Date(value);
      setEditCutoffs(prev => ({ ...prev, [episode]: localDate.toISOString() }));
    } else {
      const updated = { ...editCutoffs };
      delete updated[episode];
      setEditCutoffs(updated);
    }
  };

  const handleLineupDeadlineChange = (episode: string, value: string) => {
    if (value) {
      setEditLineupDeadlines(prev => ({ ...prev, [episode]: new Date(value).toISOString() }));
    } else {
      const updated = { ...editLineupDeadlines };
      delete updated[episode];
      setEditLineupDeadlines(updated);
    }
  };

  const handleSetCutoffsWithGemini = async () => {
    setIsResearchingCutoffs(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/research-episode-deadlines`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ season: selectedSeason }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to research episode deadlines.');
      if (data.deadlines && Object.keys(data.deadlines).length > 0) {
        setEditCutoffs(prev => ({
          ...prev,
          ...data.deadlines,
        }));
        setSuccess(`Weekly Elimination Cutoffs for Season ${selectedSeason} (Ep 2-11) set to 7:00 PM ET via Gemini! Click "Save All Settings" to save.`);
      } else {
        setError('No episode deadlines were returned by Gemini.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to set cutoffs with Gemini.');
    } finally {
      setIsResearchingCutoffs(false);
    }
  };

  const handleSetLineupDeadlinesWithGemini = async () => {
    setIsResearchingLineups(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/research-episode-deadlines`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ season: selectedSeason }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to research episode deadlines.');
      if (data.deadlines && Object.keys(data.deadlines).length > 0) {
        setEditLineupDeadlines(prev => ({
          ...prev,
          ...data.deadlines,
        }));
        setSuccess(`Active Lineup Lock Deadlines for Season ${selectedSeason} (Ep 2-11) set to 7:00 PM ET via Gemini! Click "Save All Settings" to save.`);
      } else {
        setError('No episode deadlines were returned by Gemini.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to set lineup deadlines with Gemini.');
    } finally {
      setIsResearchingLineups(false);
    }
  };

  return (
    <Container className="mt-5 mb-5">
      <div className="mb-3">
        <Link to="/admin/dashboard" className="text-decoration-none">
          &larr; Back to Admin Home
        </Link>
      </div>
      <Card className="shadow-sm home-card">
        <Card.Header as="h4" className="card-title-custom text-center">League Management</Card.Header>
        <Card.Body>
          {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}
          
          <Tabs defaultActiveKey="existing" id="league-mgmt-tabs" className="mb-4">
            <Tab eventKey="existing" title="Manage Existing Leagues">
              {isLoading ? (
                <div className="text-center p-5"><Spinner animation="border" /></div>
              ) : leagues.length === 0 ? (
                <Alert variant="info">No leagues found for Season {selectedSeason}.</Alert>
              ) : (
                <Row>
                  <Col md={3}>
                    <ListGroup className="mb-3">
                      {leagues.map(l => (
                        <ListGroup.Item 
                            key={l.id} 
                            action 
                            active={selectedLeagueId === l.id}
                            onClick={() => selectLeague(l)}
                        >
                          {l.name}
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </Col>
                  <Col md={9}>
                    {selectedLeagueId && (
                      <Card className="border-0 bg-light p-3">
                        <h5>Settings for {editName}</h5>
                        <Form>
                          <Form.Group className="mb-3">
                            <Form.Label>League Name</Form.Label>
                            <Form.Control 
                                value={editName} 
                                onChange={e => setEditName(e.target.value)} 
                            />
                          </Form.Group>
                          
                          <Form.Check 
                            type="switch"
                            id="edit-active-switch"
                            label={editActive ? "ACTIVE (Users can join)" : "INACTIVE (Join disabled)"}
                            checked={editActive}
                            onChange={(e) => setEditActive(e.target.checked)}
                            className={`mb-3 fw-bold ${editActive ? "text-success" : "text-danger"}`}
                          />

                          <Form.Check
                            type="switch"
                            id="edit-ready-to-draft-switch"
                            label={editReadyToDraft ? 'READY TO DRAFT (Visible to users)' : 'UPCOMING (Draft available soon)'}
                            checked={editReadyToDraft}
                            onChange={e => setEditReadyToDraft(e.target.checked)}
                            className={`mb-3 fw-bold ${editReadyToDraft ? 'text-success' : 'text-warning'}`}
                          />

                          <Row className="mb-3">
                            <Col md={6}>
                              <Form.Label>Draft Deadline</Form.Label>
                              <Form.Control
                                type="datetime-local"
                                value={editDeadline}
                                onChange={e => setEditDeadline(e.target.value)}
                              />
                            </Col>
                          </Row>

                          <Card className="mb-3">
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <span>Tribes</span>
                                <Button size="sm" variant="outline-primary" onClick={() => addTribe(false)}>+ Add Tribe</Button>
                            </Card.Header>
                            <Card.Body>
                                {editTribes.map((tribe, idx) => (
                                    <Row key={idx} className="mb-2">
                                        <Col><Form.Control size="sm" placeholder="Tribe Name" value={tribe.name} onChange={e => handleTribeChange(idx, 'name', e.target.value, false)} /></Col>
                                        <Col xs={3}><Form.Control size="sm" type="color" value={tribe.color} onChange={e => handleTribeChange(idx, 'color', e.target.value, false)} /></Col>
                                        <Col xs="auto"><Button size="sm" variant="outline-danger" onClick={() => removeTribe(idx, false)}>X</Button></Col>
                                    </Row>
                                ))}
                            </Card.Body>
                          </Card>

                          <Card className="mb-3">
                            <Card.Header className="d-flex justify-content-between align-items-center">
                              <span>Survivors ({editCastaways.length})</span>
                              <Button size="sm" variant="outline-primary" onClick={addEditCastaway}>+ Add Survivor</Button>
                            </Card.Header>
                            <Card.Body>
                              {editCastaways.length === 0 && <Alert variant="info">No survivors have been added to this league.</Alert>}
                              {editCastaways.map((castaway, index) => (
                                <Card key={castaway.id || `new-${index}`} className="mb-3 border-light">
                                  <Card.Body>
                                    <Row className="g-2">
                                      <Col md={4}>
                                        <Form.Label>Name</Form.Label>
                                        <Form.Control value={castaway.name} onChange={e => handleEditCastawayChange(index, 'name', e.target.value)} />
                                      </Col>
                                      <Col xs={6} md={2}>
                                        <Form.Label>Age</Form.Label>
                                        <Form.Control type="number" min="1" value={castaway.age || ''} onChange={e => handleEditCastawayChange(index, 'age', e.target.value)} />
                                      </Col>
                                      <Col xs={6} md={3}>
                                        <Form.Label>Tribe</Form.Label>
                                        <Form.Select value={castaway.tribe} onChange={e => handleEditCastawayChange(index, 'tribe', e.target.value)}>
                                          <option value="">Select tribe</option>
                                          {editTribes.filter(tribe => tribe.name.trim()).map(tribe => <option key={tribe.name} value={tribe.name}>{tribe.name}</option>)}
                                        </Form.Select>
                                      </Col>
                                      <Col md={3}>
                                        <Form.Label>Occupation</Form.Label>
                                        <Form.Control value={castaway.occupation} onChange={e => handleEditCastawayChange(index, 'occupation', e.target.value)} />
                                      </Col>
                                      <Col md={8}>
                                        <Form.Label>Image URL</Form.Label>
                                        <Form.Control type="url" value={castaway.imageUrl} onChange={e => handleEditCastawayChange(index, 'imageUrl', e.target.value)} placeholder="https://..." />
                                      </Col>
                                      <Col md={2}>
                                        <Form.Label>Preview</Form.Label>
                                        <div className="border rounded d-flex align-items-center justify-content-center bg-light" style={{ height: '38px', overflow: 'hidden' }}>
                                          {castaway.imageUrl ? <img src={getPreviewUrl(castaway.imageUrl)} alt={`${castaway.name} preview`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <span className="small text-muted">No image</span>}
                                        </div>
                                      </Col>
                                      <Col md={2} className="d-flex align-items-end">
                                        <Button className="w-100" variant="outline-danger" onClick={() => handleDeleteEditCastaway(index)}>Remove</Button>
                                      </Col>
                                    </Row>
                                  </Card.Body>
                                </Card>
                              ))}
                            </Card.Body>
                          </Card>

                          <Accordion className="mb-3">
                            <Accordion.Item eventKey="0">
                              <Accordion.Header className="d-flex align-items-center justify-content-between">
                                <span>Weekly Elimination Cutoffs</span>
                              </Accordion.Header>
                              <Accordion.Body>
                                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2 p-2 bg-light rounded border">
                                  <small className="text-muted">Set the 7:00 PM ET deadline for weekly elimination picks (Episodes 2–11).</small>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={handleSetCutoffsWithGemini}
                                    disabled={isResearchingCutoffs || isUpdating}
                                  >
                                    {isResearchingCutoffs ? (
                                      <>
                                        <Spinner animation="border" size="sm" className="me-1" /> Researching Air Dates...
                                      </>
                                    ) : (
                                      '✨ Set dates with Gemini (7 PM ET)'
                                    )}
                                  </Button>
                                </div>
                                {episodeNumbers.map(ep => (
                                  <Form.Group as={Row} key={ep} className="mb-2">
                                    <Form.Label column sm={4}>Ep {ep} Cutoff</Form.Label>
                                    <Col sm={8}>
                                      <Form.Control
                                        type="datetime-local"
                                        size="sm"
                                        value={editCutoffs[ep] ? toLocalISOString(editCutoffs[ep]) : ''}
                                        onChange={e => handleCutoffChange(String(ep), e.target.value)}
                                      />
                                    </Col>
                                  </Form.Group>
                                ))}
                              </Accordion.Body>
                            </Accordion.Item>
                          </Accordion>

                          <Accordion className="mb-3">
                            <Accordion.Item eventKey="0">
                              <Accordion.Header>Active Lineup Lock Deadlines</Accordion.Header>
                              <Accordion.Body>
                                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2 p-2 bg-light rounded border">
                                  <div>
                                    <p className="text-muted small mb-0">Set the 7:00 PM ET deadline for locking in the active 3 before each episode (Episodes 2–11+).</p>
                                  </div>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={handleSetLineupDeadlinesWithGemini}
                                    disabled={isResearchingLineups || isUpdating}
                                  >
                                    {isResearchingLineups ? (
                                      <>
                                        <Spinner animation="border" size="sm" className="me-1" /> Researching Air Dates...
                                      </>
                                    ) : (
                                      '✨ Set dates with Gemini (7 PM ET)'
                                    )}
                                  </Button>
                                </div>
                                {Array.from({ length: 13 }, (_, i) => i + 2).map(ep => (
                                  <Form.Group as={Row} key={ep} className="mb-2">
                                    <Form.Label column sm={4}>Episode {ep} lineup lock</Form.Label>
                                    <Col sm={8}>
                                      <Form.Control
                                        type="datetime-local"
                                        size="sm"
                                        value={editLineupDeadlines[String(ep)] ? toLocalISOString(editLineupDeadlines[String(ep)]) : ''}
                                        onChange={e => handleLineupDeadlineChange(String(ep), e.target.value)}
                                      />
                                    </Col>
                                  </Form.Group>
                                ))}
                              </Accordion.Body>
                            </Accordion.Item>
                          </Accordion>

                          <div className="d-flex justify-content-between">
                            <Button variant="primary" onClick={handleUpdateLeague} disabled={isUpdating}>
                                {isUpdating ? 'Saving...' : 'Save All Settings'}
                            </Button>
                            <Button variant="outline-danger" onClick={() => setShowDeleteConfirm(true)} disabled={isUpdating}>
                                Delete League
                            </Button>
                          </div>
                        </Form>
                      </Card>
                    )}
                  </Col>
                </Row>
              )}
            </Tab>
            
            <Tab eventKey="create" title="Create New League">
              <Card className="mb-4 border-primary">
                <Card.Body>
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <div>
                      <Card.Title className="mb-1">Research Season {newLeagueSeason}</Card.Title>
                      <Card.Text className="mb-0 text-muted">Use Gemini web search to fill the tribes and survivor details for review.</Card.Text>
                    </div>
                    <Button variant="outline-primary" onClick={handleResearchSeason} disabled={isResearching || isUpdating}>
                      {isResearching ? 'Researching...' : 'Search Web with Gemini'}
                    </Button>
                  </div>
                  {researchSources.length > 0 && (
                    <div className="mt-3 small">
                      <strong>Research sources:</strong>{' '}
                      {researchSources.map((source, index) => (
                        <span key={source.uri}>
                          {index > 0 && ', '}
                          <a href={source.uri} target="_blank" rel="noreferrer">{source.title}</a>
                        </span>
                      ))}
                    </div>
                  )}
                </Card.Body>
              </Card>
              <Form onSubmit={handleCreateLeague}>
                <Row>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Season</Form.Label>
                      <Form.Control
                        required
                        type="number"
                        min="1"
                        value={newLeagueSeason}
                        onChange={e => setNewLeagueSeason(Number(e.target.value))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>League Name</Form.Label>
                      <Form.Control required value={newLeagueName} onChange={e => setNewLeagueName(e.target.value)} placeholder={`e.g. Survivor Season ${selectedSeason} Pool`} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Commissioner Name</Form.Label>
                      <Form.Control required value={newCommissioner} onChange={e => setNewCommissioner(e.target.value)} placeholder="Your Name" />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>Joining Password</Form.Label>
                  <Form.Control required type="text" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Password for others to join" />
                </Form.Group>

                <Card className="mb-4">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <span>Initial Tribes</span>
                    <Button size="sm" variant="outline-secondary" onClick={() => addTribe(true)}>Add Tribe</Button>
                  </Card.Header>
                  <Card.Body>
                    {newTribes.map((tribe, index) => (
                      <Row key={index} className="mb-2">
                        <Col><Form.Control required placeholder="Tribe Name" value={tribe.name} onChange={e => handleTribeChange(index, 'name', e.target.value, true)} /></Col>
                        <Col xs={3}>
                          <Form.Control type="color" value={tribe.color} onChange={e => handleTribeChange(index, 'color', e.target.value, true)} aria-label={`Color for tribe ${index + 1}`} />
                        </Col>
                        <Col xs={3}>
                          <Form.Control required value={tribe.color} onChange={e => handleTribeChange(index, 'color', e.target.value, true)} aria-label={`Hex color for tribe ${index + 1}`} />
                        </Col>
                        <Col xs="auto"><Button variant="danger" onClick={() => removeTribe(index, true)}>X</Button></Col>
                      </Row>
                    ))}
                  </Card.Body>
                </Card>

                <Card className="mb-4">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <span>Survivors</span>
                    <Button size="sm" variant="outline-secondary" onClick={addNewCastaway}>Add Survivor</Button>
                  </Card.Header>
                  <Card.Body>
                    {newCastaways.map((castaway, index) => (
                      <Card key={index} className="mb-3 border-light">
                        <Card.Body>
                          <Row className="g-2">
                            <Col md={4}>
                              <Form.Label>Name</Form.Label>
                              <Form.Control required={index === 0} value={castaway.name} onChange={e => handleNewCastawayChange(index, 'name', e.target.value)} />
                            </Col>
                            <Col xs={6} md={2}>
                              <Form.Label>Age</Form.Label>
                              <Form.Control required={index === 0} type="number" min="1" value={castaway.age} onChange={e => handleNewCastawayChange(index, 'age', e.target.value)} />
                            </Col>
                            <Col xs={6} md={3}>
                              <Form.Label>Tribe</Form.Label>
                              <Form.Select required={index === 0} value={castaway.tribe} onChange={e => handleNewCastawayChange(index, 'tribe', e.target.value)}>
                                <option value="">Select tribe</option>
                                {newTribes.filter(tribe => tribe.name.trim()).map(tribe => (
                                  <option key={tribe.name} value={tribe.name}>{tribe.name}</option>
                                ))}
                              </Form.Select>
                            </Col>
                            <Col md={3}>
                              <Form.Label>Occupation</Form.Label>
                              <Form.Control required={index === 0} value={castaway.occupation} onChange={e => handleNewCastawayChange(index, 'occupation', e.target.value)} />
                            </Col>
                            <Col md={8}>
                              <Form.Label>Image URL</Form.Label>
                              <Form.Control type="url" value={castaway.imageUrl} onChange={e => handleNewCastawayChange(index, 'imageUrl', e.target.value)} placeholder="https://... (optional)" />
                            </Col>
                            <Col md={2}>
                              <Form.Label>Preview</Form.Label>
                              <div className="border rounded d-flex align-items-center justify-content-center bg-light" style={{ height: '56px', overflow: 'hidden' }}>
                                {castaway.imageUrl ? (
                                  <img
                                    src={getPreviewUrl(castaway.imageUrl)}
                                    alt={`${castaway.name || 'Survivor'} preview`}
                                    referrerPolicy="no-referrer"
                                    loading="lazy"
                                    decoding="async"
                                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                    onError={e => {
                                      const preview = e.currentTarget.parentElement;
                                      if (preview) {
                                        preview.replaceChildren('Image unavailable');
                                        preview.classList.add('small', 'text-muted');
                                      }
                                    }}
                                  />
                                ) : (
                                  <span className="small text-muted">No image</span>
                                )}
                              </div>
                            </Col>
                            <Col md={2} className="d-flex align-items-end">
                              <Button className="w-100" variant="outline-danger" onClick={() => removeNewCastaway(index)} disabled={newCastaways.length === 1}>Remove</Button>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    ))}
                    <Form.Text className="text-muted">
                      Add each survivor's name, age, occupation, and tribe. Image URLs are optional and can be reviewed with the preview.
                    </Form.Text>
                  </Card.Body>
                </Card>

                <div className="text-center">
                  <Button variant="success" type="submit" size="lg" disabled={isUpdating}>
                    {isUpdating ? 'Creating...' : 'Create New League'}
                  </Button>
                </div>
              </Form>
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>

      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered>
        <Modal.Header closeButton><Modal.Title>Delete League</Modal.Title></Modal.Header>
        <Modal.Body>
          Are you sure you want to delete "{editName}"? This will delete all member data and drafts. This cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleDeleteLeague}>Yes, Delete Irreversibly</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminLeagueManagement;
